import React from "react";
import { Platform, StyleSheet, View } from "react-native";
import {
  ArrowClockwiseIcon,
  ArrowRightIcon,
  LinkIcon,
  LockSimpleIcon,
  QuestionIcon,
  SparkleIcon,
  TrendUpIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { LinearGradient } from "expo-linear-gradient";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomProgressBar from "../../../../../components/common/general/CustomProgressBar/CustomProgressBar";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import Pill from "../../../../../components/common/Pill/Pill";
import type {
  CourseEvaluatorReviewCategory,
  CourseEvaluatorReviewDocumentIssueKind,
  CourseEvaluatorReviewEffectiveness,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewStage,
} from "../types/reviewTypes";
import ScoreGauge from "./ScoreGauge";
import { colors } from "../../../../../styles/design-system";

interface OverviewPanelProps {
  review: CourseEvaluatorReviewData;
  onStartReviewing: () => void;
}

function getEffectivenessPill(effectiveness: CourseEvaluatorReviewEffectiveness) {
  switch (effectiveness) {
    case "excellent":
      return { label: "Excellent", backgroundColor: "#DFF7E7", textColor: "#15703D" };
    case "fair":
      return { label: "Fair", backgroundColor: "#FFF3D6", textColor: "#B54708" };
    case "poor":
      return { label: "Poor", backgroundColor: "#FEE4E2", textColor: "#B42318" };
    case "good":
    default:
      return { label: "Good", backgroundColor: "#DCE7FF", textColor: "#1F3B8F" };
  }
}

function getReviewStageLabel(reviewStage: CourseEvaluatorReviewStage) {
  switch (reviewStage) {
    case "content-review":
      return "Content Review";
    case "full-course-review":
    default:
      return "Full Course Review";
  }
}

function getCategoryColors(category: CourseEvaluatorReviewCategory) {
  const percentage = category.maxScore > 0 ? (category.score / category.maxScore) * 100 : 0;

  if (percentage >= 80) {
    return {
      barColor: "#62C61A",
      scoreColor: "#3F7F18",
    };
  }

  if (percentage >= 60) {
    return {
      barColor: "#3E68FE",
      scoreColor: "#3E68FE",
    };
  }

  if (percentage >= 40) {
    return {
      barColor: "#DC6803",
      scoreColor: "#DC6803",
    };
  }

  return {
    barColor: "#D92D20",
    scoreColor: "#D92D20",
  };
}

function renderDocumentIssueIcon(documentIssueKind: CourseEvaluatorReviewDocumentIssueKind) {
  if (documentIssueKind === "permissionDenied") {
    return <LockSimpleIcon size={18} color="#DC6803" weight="bold" style={{ flexShrink: 0 }} />;
  }

  return <LinkIcon size={18} color="#DC6803" weight="bold" style={{ flexShrink: 0 }} />;
}

function OverviewPanel({ review, onStartReviewing }: OverviewPanelProps) {
  const effectivenessPill = getEffectivenessPill(review.effectiveness);

  return (
    <View style={styles.panel}>
      <LinearGradient
        colors={["#E6EBFF", "#F9FAFB"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.heroCard}>
        <View style={styles.heroContent}>
          <View style={styles.heroLeft}>
            <CustomText
              text={review.frameworkName}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={styles.frameworkText}
            />
            <View style={styles.heroTitleRow}>
              <CustomText
                text={`${review.courseName} (${review.sourceLabel})`}
                useTranslationText={false}
                size="xxl"
                weight="ultraBold"
                style={styles.heroTitle}
              />
              <View style={styles.reviewStagePill}>
                <CustomText
                  text={getReviewStageLabel(review.reviewStage)}
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={styles.reviewStagePillText}
                />
              </View>
            </View>
            <View style={styles.heroTags}>
              <Pill
                text={effectivenessPill.label}
                backgroundColor={effectivenessPill.backgroundColor}
                textColor={effectivenessPill.textColor}
              />
              <CustomText
                text={`Reviewed ${review.reviewedDateLabel}`}
                useTranslationText={false}
                size="xs"
                weight="normal"
                style={styles.reviewedDate}
              />
            </View>
          </View>
          <ScoreGauge score={review.score} maxScore={review.maxScore} />
        </View>
      </LinearGradient>

      <View style={styles.card}>
        <CustomText
          text="Category Breakdown"
          useTranslationText={false}
          size="s"
          weight="bold"
          style={styles.cardTitle}
        />
        <View style={styles.categoryList}>
          {review.categoryBreakdown.map((category) => {
            const categoryColors = getCategoryColors(category);

            return (
              <View key={category.name} style={styles.categoryRow}>
                <View style={styles.categoryLabelWrap}>
                  <CustomText
                    text={category.name}
                    useTranslationText={false}
                    size="s"
                    weight="normal"
                    style={styles.categoryLabel}
                  />
                  <QuestionIcon size={14} color={colors.grey[400]} weight="bold" />
                </View>
                <View style={styles.categoryBarWrap}>
                  <CustomProgressBar
                    ariaLabel={`${category.name} score`}
                    progress={(category.score / category.maxScore) * 100}
                    color={categoryColors.barColor}
                    unfilledColor="#EAECF0"
                  />
                </View>
                <CustomText
                  text={`${category.score}/${category.maxScore}`}
                  useTranslationText={false}
                  size="s"
                  weight="bold"
                  style={[styles.categoryScore, { color: categoryColors.scoreColor }]}
                />
              </View>
            );
          })}
        </View>
      </View>

      <View style={styles.twoColumnRow}>
        <View style={[styles.card, styles.twoColumnCard]}>
          <View style={styles.sectionCardHeader}>
            <SparkleIcon size={20} color="#3F7F18" />
            <CustomText
              text="Strengths"
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.strengthsTitle}
            />
          </View>
          <View style={styles.bulletList}>
            {review.strengths.map((item) => (
              <View key={item} style={styles.improvementRow}>
                <View style={[styles.improvementBullet, styles.strengthBullet]} />
                <CustomText text={item} useTranslationText={false} size="s" weight="normal" style={styles.listText} />
              </View>
            ))}
          </View>
        </View>

        <View style={[styles.card, styles.twoColumnCard]}>
          <View style={styles.sectionCardHeader}>
            <TrendUpIcon size={20} color="#3E68FE" />
            <CustomText
              text="To Improve"
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.improveTitle}
            />
          </View>
          <View style={styles.bulletList}>
            {review.improvements.map((item) => (
              <View key={item} style={styles.improvementRow}>
                <View style={styles.improvementBullet} />
                <CustomText text={item} useTranslationText={false} size="s" weight="normal" style={styles.listText} />
              </View>
            ))}
          </View>
        </View>
      </View>

      {review.documentIssues.length > 0 && (
        <View style={styles.card}>
          <View style={styles.documentHeader}>
            <WarningCircleIcon size={20} color="#DC6803" weight="fill" />
            <CustomText
              text={`${review.documentIssues.length} documents couldn't be accessed`}
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.documentHeaderText}
            />
          </View>

          <View style={styles.documentList}>
            {review.documentIssues.map((documentIssue) => (
              <View key={documentIssue.id} style={styles.documentIssueCard}>
                {renderDocumentIssueIcon(documentIssue.documentIssueKind)}
                <View style={styles.documentTextWrap}>
                  <CustomText
                    text={documentIssue.title}
                    useTranslationText={false}
                    size="s"
                    weight="normal"
                    style={styles.documentTitle}
                  />
                  <CustomText
                    text={documentIssue.message}
                    useTranslationText={false}
                    size="xs"
                    weight="normal"
                    style={styles.documentMessage}
                  />
                </View>
                <CustomButton
                  text="Retry"
                  useTranslationText={false}
                  onPress={() => {}}
                  styleType="transparent"
                  size="s"
                  leftIcon={<ArrowClockwiseIcon size={16} color={colors.grey[700]} />}
                  textStyle={styles.retryText}
                  style={styles.retryButton}
                />
              </View>
            ))}
          </View>
        </View>
      )}

      <CustomButton
        text={`Start reviewing (${review.totalCount} items)`}
        useTranslationText={false}
        onPress={onStartReviewing}
        styleType="primary"
        size="s"
        rightIcon={<ArrowRightIcon size={16} color="#FFFFFF" />}
        style={styles.startReviewingButton}
        textStyle={styles.startReviewingText}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: 16,
  },
  heroCard: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: 24,
  },
  heroContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
  },
  heroLeft: {
    flex: 1,
  },
  frameworkText: {
    color: colors.grey[700],
    marginBottom: 8,
  },
  heroTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  heroTitle: {
    color: colors.grey[900],
  },
  reviewStagePill: {
    backgroundColor: "#E6EBFF",
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    justifyContent: "center",
  },
  reviewStagePillText: {
    color: "#3459D4",
  },
  heroTags: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  reviewedDate: {
    color: colors.grey[500],
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 20,
  },
  cardTitle: {
    color: colors.grey[700],
    marginBottom: 16,
  },
  categoryList: {
    gap: 14,
  },
  categoryRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  categoryLabelWrap: {
    width: 130,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  categoryLabel: {
    color: colors.grey[700],
  },
  categoryBarWrap: {
    flex: 1,
  },
  categoryScore: {
    width: 48,
    textAlign: "right",
  },
  twoColumnRow: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    gap: 16,
  },
  twoColumnCard: {
    flex: 1,
  },
  sectionCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  strengthsTitle: {
    color: "#3F7F18",
  },
  improveTitle: {
    color: "#3E68FE",
  },
  bulletList: {
    gap: 8,
  },
  listText: {
    color: colors.grey[700],
    lineHeight: 22,
  },
  improvementRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  improvementBullet: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: "#3E68FE",
    marginTop: 7,
  },
  strengthBullet: {
    backgroundColor: "#3F7F18",
  },
  documentHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  documentHeaderText: {
    color: "#DC6803",
  },
  documentList: {
    gap: 10,
  },
  documentIssueCard: {
    backgroundColor: "#FFF8E8",
    borderWidth: 1,
    borderColor: "#FCE7B0",
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  documentTextWrap: {
    flex: 1,
  },
  documentTitle: {
    color: colors.grey[800],
    marginBottom: 2,
  },
  documentMessage: {
    color: colors.grey[500],
  },
  retryButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  retryText: {
    color: colors.grey[700],
    fontSize: 12,
    fontWeight: "500",
  },
  startReviewingButton: {
    backgroundColor: "#3E68FE",
    borderRadius: 6,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignSelf: "flex-start",
    gap: 6,
  },
  startReviewingText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
  },
});

export default OverviewPanel;
